import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Bell, Trash2, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Elenca le ricerche salvate dell'utente + permette eliminarle.
 * Mostrato in Dashboard.
 */
export default function MySavedSearches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/saved-searches');
      setItems(Array.isArray(data) ? data : (data.items || []));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Eliminare questa ricerca salvata?')) return;
    try {
      await api.delete(`/saved-searches/${id}`);
      setItems(items.filter(i => i.search_id !== id));
      toast.success('Ricerca eliminata');
    } catch { toast.error('Errore'); }
  };

  const filterSummary = (f = {}) => {
    const parts = [];
    if (f.listing_type === 'sale') parts.push('Vendita');
    if (f.listing_type === 'rent') parts.push('Affitto');
    if (f.region) parts.push(f.region);
    if (f.area_min || f.area_max) parts.push(`${f.area_min || 0}-${f.area_max || '∞'} ha`);
    if (f.price_max) parts.push(`≤ €${Math.round(f.price_max/1000)}k`);
    if (f.crops?.length) parts.push(f.crops.join(', '));
    if (f.water) parts.push('con acqua');
    return parts.join(' · ') || 'Nessun filtro';
  };

  const rebuildLink = (f = {}) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (v == null || v === '') return;
      params.set(k, Array.isArray(v) ? v.join(',') : String(v));
    });
    return `/esplora?${params.toString()}`;
  };

  if (loading) return <div className="text-sm text-muted-foreground">Carico…</div>;

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-8 text-center bg-secondary/20">
        <Bell className="mx-auto text-muted-foreground mb-3" size={22}/>
        <div className="font-medium">Nessuna ricerca salvata</div>
        <div className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Vai su Esplora, imposta i filtri (regione, area, budget, coltura) e clicca <b>Salva ricerca</b>. Riceverai email quando compare un nuovo terreno che matcha.</div>
        <Link to="/esplora"><Button size="sm" variant="outline" className="rounded-full mt-4">Vai a Esplora <ChevronRight size={14} className="ml-1"/></Button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map(s => (
        <div key={s.search_id} className="rounded-xl border border-border p-4 flex items-start gap-3 bg-card" data-testid={`saved-search-${s.search_id}`}>
          <Bell size={16} className="text-chartreuse shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-medium">{s.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{filterSummary(s.filters)}</div>
            {s.last_alert_at && (
              <div className="text-[10px] text-muted-foreground mt-1">Ultimo alert: {new Date(s.last_alert_at).toLocaleDateString('it-IT')}</div>
            )}
          </div>
          <Link to={rebuildLink(s.filters)}>
            <Button size="icon" variant="ghost" className="rounded-full h-8 w-8" title="Riapri ricerca"><Search size={13}/></Button>
          </Link>
          <Button size="icon" variant="ghost" className="rounded-full h-8 w-8 text-terracotta hover:bg-terracotta/10"
                  onClick={()=>del(s.search_id)} title="Elimina"><Trash2 size={13}/></Button>
        </div>
      ))}
    </div>
  );
}
